import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { webApi } from '../services/api';
import { isCanceledRequest } from '../utils/abortRequest';

function normalizePayoutDetails(data) {
  if (!data || typeof data !== 'object') {
    return {
      availableBalance: 0,
      employerId: null,
      minimumPayoutAmount: 500,
      bankDetails: null,
      hasBank: false
    };
  }
  const availableBalance = Number(data.balance?.availableBalance ?? data.availableBalance ?? 0);
  const employerId = data.employer?.id ?? data.employerId ?? null;
  const minimumPayoutAmount = Number(data.minimumPayoutAmount ?? 500);
  const bankDetails = data.employer?.bankDetails ?? null;

  return {
    availableBalance,
    employerId,
    minimumPayoutAmount,
    bankDetails,
    hasBank: !!bankDetails
  };
}

function formatMoney2(n) {
  const x = Number(n);
  if (Number.isNaN(x)) return '0.00';
  return x.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function parseAmountInput(text) {
  const t = String(text || '').trim();
  if (!t) return null;
  const n = parseFloat(t);
  if (Number.isNaN(n) || n < 0) return null;
  return n;
}

function WarningTriangleIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" className="payout-warn-icon">
      <path
        fill="currentColor"
        d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"
      />
    </svg>
  );
}

function PayoutPage() {
  const navigate = useNavigate();

  const [details, setDetails] = useState(() => normalizePayoutDetails(null));
  const [amountText, setAmountText] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState({ kind: '', text: '' });

  const loadDetails = async (opts = {}) => {
    const { silent = false, signal } = opts;
    if (!silent) setLoading(true);
    try {
      const axiosOpts = signal ? { signal } : {};
      const data = await webApi.getPayoutDetails('individual', axiosOpts);
      if (signal?.aborted) return;
      const norm = normalizePayoutDetails(data);
      setDetails(norm);
      setAmountText(norm.availableBalance > 0 ? String(norm.availableBalance) : '');
    } catch (err) {
      if (signal?.aborted || isCanceledRequest(err)) return;
      setBanner({ kind: 'error', text: err.message || 'Failed to load payout details' });
    } finally {
      if (!silent && !signal?.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    const ac = new AbortController();
    setBanner({ kind: '', text: '' });
    loadDetails({ signal: ac.signal });
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const available = details.availableBalance;
  const minAmt = details.minimumPayoutAmount;
  const parsed = parseAmountInput(amountText);
  const amountValid =
    parsed !== null &&
    parsed >= minAmt &&
    parsed <= available &&
    available > 0;
  const canProceed = amountValid && details.hasBank && !loading && !submitting;

  const titleMain = 'Individual Payout';
  const titleSub = 'Settle amount to your bank account';

  const showValidation = banner.text && banner.kind === 'error';

  const submit = async (e) => {
    e.preventDefault();
    if (!canProceed) return;
    setSubmitting(true);
    setBanner({ kind: '', text: '' });
    try {
      await webApi.createIndividualPayout({
        transferAmount: parsed,
        employerId: Number(details.employerId),
        transferNotes: ''
      });
      await loadDetails({ silent: true });
      setBanner({
        kind: 'success',
        text: `Payout of ₹${formatMoney2(parsed)} initiated successfully.`
      });
    } catch (err) {
      setBanner({ kind: 'error', text: err.message || 'Payout failed' });
    } finally {
      setSubmitting(false);
    }
  };

  const clearAmount = () => {
    setAmountText('');
  };

  const accountLast4 = details.bankDetails?.accountNumber
    ? String(details.bankDetails.accountNumber).replace(/\s/g, '').slice(-4)
    : '';

  return (
    <div className="payout-page">
      <header className="payout-topbar">
        <button
          type="button"
          className="payout-back"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="currentColor" d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
        </button>
        <h1 className="payout-topbar-title">Payout</h1>
        <span className="payout-topbar-spacer" aria-hidden="true" />
      </header>

      <div className="payout-body">
        {loading ? (
          <p className="payout-loading">Loading…</p>
        ) : (
          <form className="payout-form" onSubmit={submit}>
            <div className="payout-intro">
              <h2 className="payout-h2">{titleMain}</h2>
              <p className="payout-sub">{titleSub}</p>
            </div>

            <label className="payout-field-label" htmlFor="payout-amount-input">
              Payout Amount
            </label>
            <div className="payout-input-shell">
              <span className="payout-rupee" aria-hidden="true">
                ₹
              </span>
              <input
                id="payout-amount-input"
                className="payout-amount-input"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                placeholder="0.00"
                value={amountText}
                onChange={(e) => {
                  setAmountText(e.target.value);
                  if (banner.kind === 'success') setBanner({ kind: '', text: '' });
                }}
              />
              {amountText.trim() ? (
                <button
                  type="button"
                  className="payout-clear"
                  onClick={clearAmount}
                  aria-label="Clear amount"
                >
                  ×
                </button>
              ) : null}
            </div>
            <div className="payout-helper-row">
              <p className="payout-helper-text">
                Minimum ₹{formatMoney2(minAmt)}, Maximum based on available balance
              </p>
              <p className="payout-available">
                Available: ₹{formatMoney2(available)}
              </p>
            </div>

            {details.bankDetails ? (
              <section className="payout-bank-block" aria-label="Bank account">
                <h3 className="payout-bank-heading">Bank Account Details</h3>
                <div className="payout-bank-card">
                  <p className="payout-bank-name">{details.bankDetails.bankName || 'Bank'}</p>
                  <p className="payout-bank-meta">
                    {accountLast4 ? `****${accountLast4}` : 'Account'}
                  </p>
                  <p className="payout-bank-meta">{details.bankDetails.accountHolderName}</p>
                </div>
              </section>
            ) : (
              <div className="payout-warning-card">
                <WarningTriangleIcon />
                <div className="payout-warning-body">
                  <p className="payout-warning-title">Bank Account Required</p>
                  <p className="payout-warning-desc">
                    You need to add and verify your bank account to receive payouts.
                  </p>
                  <Link to="/bank-accounts" className="payout-warning-btn">
                    Add Bank Account
                  </Link>
                </div>
              </div>
            )}

            {showValidation ? (
              <p className={`payout-validation payout-validation--${banner.kind}`} role="alert">
                {banner.text}
              </p>
            ) : null}
            {banner.kind === 'success' && banner.text ? (
              <p className="payout-validation payout-validation--success" role="status">
                {banner.text}
              </p>
            ) : null}

            <button
              type="submit"
              className="payout-proceed"
              disabled={!canProceed}
            >
              {submitting ? 'Processing…' : 'Proceed with Payout'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default PayoutPage;
